import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * ContextPanel — assinatura do AMAI OPS.
 *
 * Consulta rápida em painel lateral de 460px: a tela de origem CONTINUA
 * visível atrás. É por isso que consulta nunca abre modal aqui — o operador
 * precisa manter a lista no campo de visão enquanto confere um registro.
 */
export function ContextPanel({
  open,
  onClose,
  title,
  subtitle,
  tabs,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  tabs?: string[];
  children: ReactNode | ((activeTab: number) => ReactNode);
  footer?: ReactNode;
}) {
  const [tab, setTab] = useState(0);
  const [shownFor, setShownFor] = useState(title);

  // Trocar de registro volta para a primeira aba. Ajustar o estado durante o
  // render (em vez de num efeito) evita o flash da aba antiga no registro novo.
  if (title !== shownFor) {
    setShownFor(title);
    setTab(0);
  }

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="overlay" onClick={onClose} aria-hidden />
      <aside className="ctxpanel" role="dialog" aria-modal="false" aria-label={title}>
        <div className="ctxpanel__head">
          <div className="ctxpanel__titlerow">
            <div>
              <div className="ctxpanel__title">{title}</div>
              {subtitle && <div className="ctxpanel__subtitle mono">{subtitle}</div>}
            </div>
            <button type="button" className="ctxpanel__close" onClick={onClose} aria-label="Fechar">
              <X size={18} aria-hidden />
            </button>
          </div>

          {tabs && (
            <div className="ctxpanel__tabs" role="tablist">
              {tabs.map((label, index) => (
                <button
                  key={label}
                  type="button"
                  role="tab"
                  className="ctxpanel__tab"
                  data-state={tab === index ? 'active' : 'inactive'}
                  aria-selected={tab === index}
                  onClick={() => setTab(index)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ctxpanel__body">
          {typeof children === 'function' ? children(tab) : children}
        </div>

        {footer && <div className="ctxpanel__footer">{footer}</div>}
      </aside>
    </>
  );
}

/** Par rótulo/valor do painel. `mono` para documento, data, telefone e valor. */
export function ReadField({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="readfield">
      <div className="readfield__label">{label}</div>
      <div className={`readfield__value${mono ? ' mono' : ''}`}>{value}</div>
    </div>
  );
}

export function StatTiles({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className="stat-row">
      {items.map((item) => (
        <div key={item.label} className="stat">
          <div className="stat__label">{item.label}</div>
          <div className="stat__value">{item.value}</div>
        </div>
      ))}
    </div>
  );
}
